import { Num, T } from 'gt-react';
import type { LegPriceBreak } from '@erp/shared-types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  AssignmentLine,
  OptionForwardBuyDetail,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { CostSourceBadge } from './CostSourceBadge';
import { OptionRationale } from './OptionRationale';

/**
 * The forward-buy state of one board row, display only
 * (docs/procurement/forward-buying.md, "Board surfaces").
 *
 * When the line's effective option is an uplift, the row itself says what the
 * quantity is made of (the engine's ordinary quantity, the units forward
 * buying added, and the total being bought), and a coverage chip says how far
 * ahead that buys, with the engine's rationale sentence one hover away. A line
 * forward buying pulled forward (a SKU with no open need yet) has no ordinary
 * quantity, so its row says so instead.
 * Nothing renders on any other row, which is almost every row. The only action
 * on forward buying is the cart-level removal in the supplier's detail view.
 *
 * Coverage is counted in DAYS AFTER THE ORDER ARRIVES, and the chip shows both
 * numbers because they answer different questions: the total is what the line
 * now covers, the extra is what forward buying is responsible for.
 */

const CHIP_CLASS =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums';

function CoverageTooltip({
  forwardBuy,
  priceBreak,
}: {
  forwardBuy: OptionForwardBuyDetail;
  /** The uplift's buy leg provenance: what priced the raised quantity. */
  priceBreak: LegPriceBreak | null;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-300">
        <T context="replenishment forward-buy tooltip heading">Forward buy</T>
      </div>
      {forwardBuy.rationale != null && (
        // The engine's own sentence, shown verbatim (decision 12) except for
        // the contract clause, which reads in the buyer's date format.
        <div className="text-sm">
          <OptionRationale rationale={forwardBuy.rationale} />
        </div>
      )}
      <div className="text-sm">
        {forwardBuy.pulledForward ? (
          <T context="replenishment forward-buy tooltip for a SKU with no open need that forward buying pulled into the cart: the quantity bought now, and the coverage it buys past the order's arrival compared with the stock already there">
            Not due yet: buying <Num>{forwardBuy.upliftQuantity}</Num> now,
            covering <Num>{forwardBuy.totalCoverageDays}</Num> days after
            arrival instead of{' '}
            <Num>
              {forwardBuy.totalCoverageDays - forwardBuy.extraCoverageDays}
            </Num>
            .
          </T>
        ) : (
          <T context="replenishment forward-buy tooltip: the quantity before and after the uplift, and the coverage it buys past the order's arrival">
            Raised from <Num>{forwardBuy.baseQuantity}</Num> to{' '}
            <Num>{forwardBuy.upliftQuantity}</Num>, covering{' '}
            <Num>{forwardBuy.totalCoverageDays}</Num> days after arrival instead
            of{' '}
            <Num>
              {forwardBuy.totalCoverageDays - forwardBuy.extraCoverageDays}
            </Num>
            .
          </T>
        )}
      </div>
      <CostSourceBadge priceBreak={priceBreak} tone="inverse" />
    </div>
  );
}

interface ForwardBuyLineIndicatorProps {
  line: AssignmentLine;
  className?: string;
}

export function ForwardBuyLineIndicator({
  line,
  className,
}: ForwardBuyLineIndicatorProps) {
  const forwardBuy = line.topOption?.forwardBuy ?? null;
  if (forwardBuy == null) return null;

  return (
    <div className={cn('flex flex-col items-end gap-0.5', className)}>
      <span className="whitespace-nowrap text-[11px] tabular-nums text-sky-700">
        {forwardBuy.pulledForward ? (
          <T context="replenishment line under the order quantity: the whole quantity was pulled forward by forward buying from a later order, because the SKU is not due yet">
            <Num>{forwardBuy.upliftQuantity}</Num> pulled forward, not due yet
          </T>
        ) : (
          <T context="replenishment line under the order quantity: the engine's ordinary quantity, plus the units forward buying added, equals the quantity being ordered">
            <Num>{forwardBuy.baseQuantity}</Num> base +{' '}
            <Num>{forwardBuy.upliftQuantity - forwardBuy.baseQuantity}</Num>{' '}
            forward buy = <Num>{forwardBuy.upliftQuantity}</Num>
          </T>
        )}
      </span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(CHIP_CLASS, 'bg-sky-50 text-sky-700')}>
              <T context="replenishment line chip: total days of stock this line now covers after it arrives, and how many of those forward buying added">
                <Num>{forwardBuy.totalCoverageDays}</Num>d total, +
                <Num>{forwardBuy.extraCoverageDays}</Num>d
              </T>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" align="end" className="max-w-xs">
            <CoverageTooltip
              forwardBuy={forwardBuy}
              priceBreak={line.topOption?.priceBreak ?? null}
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
